<template>
  <Stack class="stk-py-8 stk-gap-6">
    <div class="cases-grid">
      <Box class="cases-list">
        <BoxHead>
          <Title>Cases</Title>
        </BoxHead>
        <div class="cases-toolbar">
          <Button type="primary" @click="startNewCase">Create case</Button>
          <Button
            type="secondary"
            icon-before="fas fa-redo-alt"
            :disabled="isLoadingCases"
            title="Refresh"
            @click="fetchCases"
          />
        </div>
        <div class="cases-list-controls">
          <div class="cases-control-section">
            <span class="cases-control-label">Filter By</span>
            <div class="cases-control-group">
              <button
                class="cases-mini-button"
                type="button"
                :class="{ active: listFilter === 'all' }"
                :disabled="isLoadingCases"
                @click="setListFilter('all')"
              >
                All
              </button>
              <button
                class="cases-mini-button"
                type="button"
                :class="{ active: listFilter === 'new' }"
                :disabled="isLoadingCases"
                @click="setListFilter('new')"
              >
                New
              </button>
              <button
                class="cases-mini-button"
                type="button"
                :class="{ active: listFilter === 'closed' }"
                :disabled="isLoadingCases"
                @click="setListFilter('closed')"
              >
                Closed
              </button>
            </div>
          </div>
          <div class="cases-control-section">
            <span class="cases-control-label">Sort By</span>
            <div class="cases-control-group">
              <button
                class="cases-mini-button"
                type="button"
                :class="{ active: listSortBy === 'updated_at' }"
                :disabled="isLoadingCases"
                :title="`Sort by updated date (${listSortDirection})`"
                @click="setListSort('updated_at')"
              >
                Updated
              </button>
              <button
                class="cases-mini-button"
                type="button"
                :class="{ active: listSortBy === 'created_at' }"
                :disabled="isLoadingCases"
                :title="`Sort by created date (${listSortDirection})`"
                @click="setListSort('created_at')"
              >
                Created
              </button>
            </div>
          </div>
        </div>
        <LoaderWithText v-if="isLoadingCases">
          Loading cases…
        </LoaderWithText>
        <div v-else-if="cases.length === 0" class="empty-state">
          No cases found for this entity.
        </div>
        <div v-else-if="visibleCases.length === 0" class="empty-state">
          No cases match current filters.
        </div>
        <ul v-else class="cases-items">
          <li
            v-for="caseItem in visibleCases"
            class="cases-item"
            :class="{ active: caseItem.id === selectedCaseId }"
            @click="selectCase(caseItem.id)"
          >
            <div class="cases-item-header">
              <span class="cases-item-number">#{{ caseItem.case_number }}</span>
              <Badge :status="getCaseStatusBadgeStatus(caseItem.status)" resizable>
                {{ caseItem.status || '-' }}
              </Badge>
            </div>
            <div class="cases-item-subject">{{ caseItem.subject }}</div>
            <div class="cases-item-meta">
              <Badge resizable>
                {{ getCaseTypeLabel(caseItem) }}
              </Badge>
              <Badge :status="getCasePriorityBadgeStatus(caseItem.priority)" resizable>
                {{ caseItem.priority || '-' }}
              </Badge>
            </div>
          </li>
        </ul>
      </Box>
      <Box class="cases-detail">
        <div v-if="showCaseForm">
          <CaseSubmissionForm
            :submit-url="`/api/v1/entities/${entityExternalId}/cases/`"
            :show-client-selector="false"
            :locked-entity-name="entity?.name || entityExternalId"
            :locked-entity-external-id="entityExternalId"
            @submitted="handleCaseFormSubmitted"
            @cancel="cancelNewCase"
          />
        </div>
        <CaseDetailPanel
          v-else
          :selected-case="selectedCase"
          :comments="comments"
          :attachments="attachments"
          :is-loading-detail="isLoadingDetail"
          :is-submitting-comment="isSubmittingComment"
          :is-submitting-attachment="isSubmittingAttachment"
          :attachment-error="attachmentError"
          :comment-error="commentError"
          :new-comment="newComment"
          @refresh-detail="refreshSelectedCase"
          @update:new-comment="updateNewComment"
          @submit-comment="submitComment"
          @submit-attachment="submitAttachment"
        />
      </Box>
    </div>
  </Stack>
</template>

<script>
import {
  Badge,
  Box,
  BoxHead,
  Button,
  LoaderWithText,
  Stack,
  Title,
} from '@stakeui';
import CaseSubmissionForm from '@frontend/ui/features/example/components/CaseSubmissionForm.vue';
import CaseDetailPanel from '@frontend/ui/features/example/components/CaseDetailPanel.vue';
import {
  getCaseTypeLabel,
  getCasePriorityBadgeStatus,
  getCaseStatusBadgeStatus,
} from '@frontend/ui/features/example/utils/caseBadges';
import {
  postCaseComment,
  uploadCaseAttachments,
} from '@frontend/ui/features/example/utils/caseActivityApi';
import * as Sentry from '@sentry/vue';

export default {
  name: 'EntityCases',
  components: {
    Badge,
    Box,
    BoxHead,
    Button,
    LoaderWithText,
    Stack,
    CaseDetailPanel,
    CaseSubmissionForm,
    Title,
  },
  props: {
    entityExternalId: {
      type: String,
      required: true,
    },
    entity: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      cases: [],
      selectedCase: null,
      selectedCaseId: null,
      comments: [],
      attachments: [],
      isLoadingCases: false,
      isLoadingDetail: false,
      isSubmittingComment: false,
      isSubmittingAttachment: false,
      showCaseForm: false,
      attachmentError: '',
      commentError: '',
      newComment: '',
      refreshIntervalId: null,
      listFilter: 'all',
      listSortBy: 'updated_at',
      listSortDirection: 'desc',
    };
  },
  computed: {
    visibleCases() {
      const filteredCases = this.cases.filter(caseItem => {
        if (this.listFilter === 'closed') {
          return this.isClosedCase(caseItem);
        }
        if (this.listFilter === 'new') {
          return this.isNewCase(caseItem);
        }
        return true;
      });

      const sortedCases = [...filteredCases];
      const direction = this.listSortDirection === 'asc' ? 1 : -1;
      return sortedCases.sort((a, b) => {
        const timestampA = this.getCaseTimestampByField(a, this.listSortBy);
        const timestampB = this.getCaseTimestampByField(b, this.listSortBy);
        if (timestampA === timestampB) {
          const fallbackA = this.getCaseTimestampByField(a, 'updated_at');
          const fallbackB = this.getCaseTimestampByField(b, 'updated_at');
          return (fallbackA - fallbackB) * direction;
        }
        return (timestampA - timestampB) * direction;
      });
    },
  },
  created() {
    if (this.$route?.query?.attachmentUploadFailed === '1') {
      this.reportAttachmentUploadFailure({
        source: 'route_query_attachment_upload_failed',
      });
      this.attachmentError = 'Attachment failed to upload, please try again.';
    }
    this.fetchCases();
  },
  mounted() {
    this.refreshIntervalId = setInterval(() => {
      this.refreshSelectedCase({ silent: true });
    }, 15000);
  },
  beforeDestroy() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
  },
  methods: {
    getCaseTypeLabel,
    getCaseStatusBadgeStatus,
    getCasePriorityBadgeStatus,
    reportAttachmentUploadFailure({ source, error = null, files = [], context = {} } = {}) {
      const normalizedFiles = Array.isArray(files) ? files : [];
      const fileDetails = normalizedFiles.map(file => ({
        name: file?.name || '',
        size: file?.size || 0,
        type: file?.type || '',
      }));
      const sentryContext = {
        source: source || 'unknown',
        entityExternalId: this.entityExternalId || null,
        selectedCaseId: this.selectedCaseId || null,
        selectedCaseNumber: this.selectedCase?.case_number || null,
        routeName: this.$route?.name || null,
        routePath: this.$route?.fullPath || null,
        attachmentUploadFailedFlag: this.$route?.query?.attachmentUploadFailed === '1',
        fileCount: normalizedFiles.length,
        fileDetails,
        uploadContext: error?.uploadContext || null,
        rootCause: error?.cause?.message || error?.message || '',
        ...context,
      };

      if (error) {
        Sentry.captureException(error, {
          tags: {
            feature: 'entity_cases',
            operation: 'upload_case_attachment',
            surface: 'attachment_error_banner',
          },
          extra: sentryContext,
        });
        return;
      }

      Sentry.captureMessage('Attachment failed to upload shown to user', {
        level: 'warning',
        tags: {
          feature: 'entity_cases',
          operation: 'upload_case_attachment',
          surface: 'attachment_error_banner',
        },
        extra: sentryContext,
      });
    },
    normalizeCaseStatus(caseItem) {
      return String(caseItem?.status || '').trim().toLowerCase();
    },
    isClosedCase(caseItem) {
      const normalizedStatus = this.normalizeCaseStatus(caseItem);
      return normalizedStatus === 'closed' || normalizedStatus === 'resolved';
    },
    isNewCase(caseItem) {
      return this.normalizeCaseStatus(caseItem) === 'new';
    },
    getCaseTimestampByField(caseItem, fieldName) {
      const timestampValue = caseItem?.[fieldName];
      if (!timestampValue) {
        return 0;
      }
      return Date.parse(timestampValue) || 0;
    },
    getMostRecentCase(cases = []) {
      if (!Array.isArray(cases) || cases.length === 0) {
        return null;
      }
      return cases.reduce((latest, current) => {
        if (!latest) {
          return current;
        }
        const latestTimestamp = Date.parse(latest.updated_at || latest.created_at || '') || 0;
        const currentTimestamp = Date.parse(current.updated_at || current.created_at || '') || 0;
        return currentTimestamp > latestTimestamp ? current : latest;
      }, null);
    },
    setListFilter(filterValue) {
      this.listFilter = filterValue;
    },
    setListSort(fieldName) {
      if (this.listSortBy === fieldName) {
        this.listSortDirection = this.listSortDirection === 'desc' ? 'asc' : 'desc';
        return;
      }
      this.listSortBy = fieldName;
      this.listSortDirection = 'desc';
    },
    async fetchCases() {
      this.isLoadingCases = true;
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/cases/`);
        if (!response.ok) {
          throw new Error('Failed to fetch cases');
        }
        const data = await response.json();
        this.cases = data.cases || [];
        if (this.selectedCaseId) {
          const stillExists = this.cases.find(item => item.id === this.selectedCaseId);
          if (!stillExists) {
            this.selectedCaseId = null;
            this.selectedCase = null;
            this.comments = [];
            this.attachments = [];
          }
        }
        const routeCaseId = this.$route?.query?.caseId;
        if (!this.selectedCaseId && !this.showCaseForm && this.cases.length > 0) {
          let routeTargetCase = null;
          if (routeCaseId) {
            routeTargetCase = this.cases.find(item => item.id === routeCaseId) || null;
          }
          const mostRecentCase = this.getMostRecentCase(this.cases);
          const targetCase = routeTargetCase || mostRecentCase;
          if (targetCase?.id) {
            this.selectCase(targetCase.id);
          }
        }
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      } finally {
        this.isLoadingCases = false;
      }
    },
    async selectCase(caseId) {
      this.selectedCaseId = caseId;
      this.showCaseForm = false;
      this.commentError = '';
      await this.fetchCaseDetail(caseId);
    },
    async fetchCaseDetail(caseId, { silent = false } = {}) {
      if (!silent) {
        this.isLoadingDetail = true;
      }
      try {
        const response = await fetch(`/api/v1/entities/${this.entityExternalId}/cases/${caseId}/`);
        if (!response.ok) {
          throw new Error('Failed to fetch case detail');
        }
        const data = await response.json();
        this.selectedCase = data.case;
        this.comments = data.comments || [];
        this.attachments = data.attachments || [];
        this.commentError = '';
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
      } finally {
        if (!silent) {
          this.isLoadingDetail = false;
        }
      }
    },
    async refreshSelectedCase({ silent = true } = {}) {
      if (!this.selectedCaseId || this.showCaseForm || this.isLoadingDetail) {
        return;
      }
      if (document.hidden) {
        return;
      }
      if (this.isSubmittingAttachment) {
        return;
      }
      await this.fetchCaseDetail(this.selectedCaseId, { silent });
    },
    startNewCase() {
      this.showCaseForm = true;
      this.selectedCase = null;
      this.selectedCaseId = null;
      this.comments = [];
      this.attachments = [];
      this.attachmentError = '';
      this.commentError = '';
    },
    cancelNewCase() {
      this.showCaseForm = false;
      this.attachmentError = '';
      this.commentError = '';
    },
    async handleCaseFormSubmitted(payload) {
      this.showCaseForm = false;
      const data = payload?.data || payload;
      const attachmentUploadFailed = Boolean(payload?.attachmentUploadFailed);
      const submittedCase = data?.case || data || null;
      this.selectedCase = submittedCase;
      this.selectedCaseId = submittedCase?.id || null;
      this.newComment = '';
      if (attachmentUploadFailed) {
        this.reportAttachmentUploadFailure({
          source: 'case_submission_payload_attachment_upload_failed',
          context: {
            submittedCaseId: submittedCase?.id || null,
            submittedCaseNumber: submittedCase?.case_number || null,
          },
        });
      }
      this.attachmentError = attachmentUploadFailed ? 'Attachment failed to upload, please try again.' : '';
      this.commentError = '';
      await this.fetchCases();
      if (this.selectedCaseId) {
        await this.fetchCaseDetail(this.selectedCaseId);
      }
    },
    updateNewComment(value) {
      this.newComment = value;
      this.commentError = '';
    },
    async submitComment() {
      if (!this.selectedCaseId || !this.newComment.trim()) {
        return;
      }
      this.commentError = '';
      this.isSubmittingComment = true;
      try {
        const data = await postCaseComment({
          url: `/api/v1/entities/${this.entityExternalId}/cases/${this.selectedCaseId}/comments/`,
          body: this.newComment,
          csrfToken: this.$root.$data.csrfToken,
        });
        this.comments = data.comments || [];
        this.newComment = '';
        this.commentError = '';
      } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        this.commentError = 'Comment failed to post, please try again.';
      } finally {
        this.isSubmittingComment = false;
      }
    },
    async submitAttachment(filesToUpload = []) {
      if (!this.selectedCaseId) {
        this.attachmentError = 'Select a case before uploading attachments.';
        return;
      }
      const files = (Array.isArray(filesToUpload) && filesToUpload.length) ? filesToUpload : [];
      if (!files.length) {
        return;
      }
      this.attachmentError = '';
      this.isSubmittingAttachment = true;
      try {
        const data = await uploadCaseAttachments({
          url: `/api/v1/entities/${this.entityExternalId}/cases/${this.selectedCaseId}/attachments/`,
          files,
          csrfToken: this.$root.$data.csrfToken,
        });
        this.attachments = data?.attachments || this.attachments;
      } catch (error) {
        console.error(error);
        this.reportAttachmentUploadFailure({
          source: 'submit_attachment_error',
          error,
          files,
        });
        this.attachmentError = 'Attachment failed to upload, please try again.';
      } finally {
        this.isSubmittingAttachment = false;
      }
    },
  },
};
</script>

<style scoped>
@import '../styles/cases-shared.css';

.cases-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: var(--stk-size-6);
}

.cases-items {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 70vh;
  overflow-y: auto;
  padding-right: var(--stk-size-1);
}

.cases-list-controls {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--stk-size-2);
  margin-bottom: var(--stk-size-3);
}

.cases-control-section {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--stk-size-2);
  width: 100%;
}

.cases-control-label {
  color: var(--stk-color-text-muted);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  min-width: 2.75rem;
  flex: 0 0 auto;
}

.cases-control-group {
  display: flex;
  gap: 0.375rem;
  flex-wrap: nowrap;
  min-width: 0;
}

.cases-mini-button {
  appearance: none;
  border: 1px solid var(--stk-color-border);
  border-radius: 999px;
  padding: 0.125rem 0.45rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--stk-color-text-muted);
  background: var(--stk-color-white);
  cursor: pointer;
  transition: all 120ms ease-in-out;
}

.cases-mini-button:hover:not(:disabled) {
  border-color: var(--stk-primary);
  color: var(--stk-primary);
}

.cases-mini-button.active {
  color: var(--stk-primary);
  border-color: var(--stk-primary);
  background: #f3f7ff;
}

.cases-mini-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cases-item {
  padding: var(--stk-size-4);
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
  margin-bottom: var(--stk-size-3);
  cursor: pointer;
}

.cases-item.active {
  border-color: #d7dce4;
  background: #f4f6f8;
}

.cases-item-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  color: var(--stk-color-text-muted);
}

.cases-item-subject {
  font-weight: 600;
  margin-top: var(--stk-size-2);
}

.cases-item-meta {
  display: flex;
  gap: var(--stk-size-3);
  font-size: 0.8rem;
  color: var(--stk-color-text-muted);
  margin-top: var(--stk-size-2);
}

</style>
